import { useLoaderData } from "react-router";

import { EditorScreen, type EditorSource } from "../editor/EditorScreen.js";
import type { MeResponse } from "../api/client.js";
import {
  createDraft,
  getDraft,
  resolveActiveDraft,
  setCurrentDraftId,
  type LocalDraft,
} from "../local/draftStore.js";
import { STARTER_TEMPLATES } from "@website-builder/editor";

interface LocalEditorData {
  readonly source: EditorSource;
  readonly session: MeResponse | null;
  readonly resumePublish: boolean;
}

/** Best-effort session — the builder works fully without one. */
async function loadSession(): Promise<MeResponse | null> {
  try {
    const r = await fetch("/api/me", { credentials: "same-origin" });
    if (!r.ok) return null;
    return (await r.json()) as MeResponse;
  } catch {
    return null;
  }
}

function draftToSource(d: LocalDraft): EditorSource {
  return {
    id: d.id,
    name: d.name,
    slug: d.slug,
    role: "owner",
    isLocal: true,
    templateId: d.templateId,
    ...(d.cloudSiteId ? { cloudSiteId: d.cloudSiteId } : {}),
    ...(d.cloudSlug ? { cloudSlug: d.cloudSlug } : {}),
  };
}

/**
 * Loader for `/` — the free, no-login editor home. Resolves which local draft
 * to open (explicit ?draft=/?publish=, else the active/most-recent one),
 * creating a first draft if the visitor has none. Never gates on a session.
 */
export async function localEditorLoader({
  request,
}: {
  request: Request;
}): Promise<LocalEditorData> {
  const url = new URL(request.url);
  const wantId =
    url.searchParams.get("publish") ?? url.searchParams.get("draft");

  let draft = wantId ? getDraft(wantId) : resolveActiveDraft();
  if (!draft) {
    draft = createDraft({
      name: "Untitled site",
      templateId: STARTER_TEMPLATES[0]!.id,
    });
  }
  setCurrentDraftId(draft.id);

  const session = await loadSession();
  return {
    source: draftToSource(draft),
    session,
    resumePublish: url.searchParams.has("publish"),
  };
}

export function LocalEditor() {
  const { source, session, resumePublish } = useLoaderData() as LocalEditorData;
  // key on id so switching drafts (?draft=…) fully remounts the editor doc.
  return (
    <EditorScreen
      key={source.id}
      source={source}
      session={session}
      resumePublish={resumePublish}
    />
  );
}
